/**
 * SectionDetailsDialog Component
 *
 * Displays detailed information about a section including:
 * - Section thumbnail image
 * - Section name and description
 * - List of expected items with quantities
 * - Breakdown of item types and total quantity
 */

import React, { useEffect } from 'react';
import { RoomSection } from '@/types';
import { Package, Image as ImageIcon } from 'lucide-react';
import { useCategoryContext } from '@/contexts/CategoryContext';
import { getClassColor } from '@/utils/colorUtils';
import ThumbnailPreview from '@/components/ThumbnailPreview';
import { formatClassNameForDisplay } from '@/utils/classNameUtils';

interface SectionDetailsDialogProps {
  section: RoomSection;
  onClose: () => void;
}

export const SectionDetailsDialog: React.FC<SectionDetailsDialogProps> = ({
  section,
  onClose,
}) => {
  const { getClassCategory, getClassThumbnail } = useCategoryContext();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Calculate totals
  const totalTypes = section.expected_items.length;
  const totalQuantity = section.expected_items.reduce((sum, item) => sum + item.expected_quantity, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#ffffff' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 z-10" style={{ backgroundColor: '#ffffff' }}>
          <h2 className="text-2xl font-bold text-gray-900">Section Details</h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Section Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section Name
            </label>
            <div className="text-lg font-semibold text-gray-900">
              {section.name}
            </div>
          </div>

          {/* Section Description */}
          {section.description && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <div className="text-gray-600">
                {section.description}
              </div>
            </div>
          )}

          {/* Section Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Image
            </label>
            <div className="rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50">
              {section.image_path ? (
                <img
                  src={section.image_path}
                  alt={section.name}
                  className="w-full h-auto max-h-96 object-contain"
                />
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center text-gray-400">
                  <ImageIcon className="h-12 w-12 mb-2" />
                  <span className="text-sm">No reference image</span>
                </div>
              )}
            </div>
          </div>

          {/* Expected Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Expected Items
            </label>

            {section.expected_items.length > 0 ? (
              <>
                <div className="space-y-2 mb-4">
                  {section.expected_items.map((item, index) => {
                    const category = getClassCategory(item.class_name);
                    const color = getClassColor(item.class_name, category);

                    return (
                      <div
                        key={item.id || index}
                        className="flex items-center justify-between p-3 rounded-full border-2"
                        style={{
                          borderColor: color,
                          backgroundColor: `${color}15`,
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {category?.icon && (
                            <Package className="h-4 w-4 flex-shrink-0" style={{ color }} />
                          )}
                          <ThumbnailPreview
                            className={item.class_name}
                            thumbnailUrl={getClassThumbnail(item.class_name)?.url}
                          >
                            <span className="font-medium truncate" style={{ color }}>
                              {formatClassNameForDisplay(item.class_name)}
                            </span>
                          </ThumbnailPreview>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-lg font-bold" style={{ color }}>
                            {item.expected_quantity}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Breakdown Summary */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Total Item Types</div>
                      <div className="text-2xl font-bold text-primary-700">{totalTypes}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Total Quantity</div>
                      <div className="text-2xl font-bold text-primary-700">{totalQuantity}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No expected items defined for this section
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionDetailsDialog;
